import type { TableauEnvironment } from '../tableau/init';
import { findWorksheet, getSelectedMarks, subscribeToSelectionChanges } from '../tableau/worksheet';
import { mapSelectedMark } from '../tableau/mapping';
import { sendDebugLog } from '../dev/debug';
import { getAvailableWorksheets } from '../tableau/settings';

function getSortedEntries(fields: Record<string, unknown>): Array<[string, unknown]> {
  return Object.entries(fields).sort(([leftField], [rightField]) => {
    if (leftField === 'alert_id') return -1;
    if (rightField === 'alert_id') return 1;
    return leftField.localeCompare(rightField);
  });
}

function createEmptyState(message: string): HTMLDivElement {
  const element = document.createElement('div');
  element.className = 'inspector-empty';
  element.textContent = message;
  return element;
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined || value === '') {
    return '(empty)';
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
}

function renderFields(container: HTMLElement, fields: Record<string, unknown>): void {
  container.innerHTML = '';

  const entries = getSortedEntries(fields);
  if (entries.length === 0) {
    container.appendChild(createEmptyState('Select a single alert row from Alerts List.'));
    return;
  }

  for (const [field, value] of entries) {
    const row = document.createElement('div');
    row.className = 'inspector-row';

    const label = document.createElement('div');
    label.className = 'inspector-label';
    label.textContent = field;

    const displayValue = document.createElement('div');
    displayValue.className = 'inspector-value';
    displayValue.textContent = formatValue(value);

    row.appendChild(label);
    row.appendChild(displayValue);
    container.appendChild(row);
  }
}

export function createAlertInspector(
  environment: TableauEnvironment,
  worksheetName: string
): void {
  const root = document.getElementById('app');
  if (!root) {
    return;
  }

  root.innerHTML = '';

  const container = document.createElement('div');
  container.className = 'inspector-container';

  const title = document.createElement('h2');
  title.className = 'inspector-title';
  title.textContent = 'Selected Alert';

  const subtitle = document.createElement('div');
  subtitle.className = 'inspector-subtitle';
  subtitle.textContent = `Worksheet: ${worksheetName}`;

  const meta = document.createElement('div');
  meta.className = 'inspector-meta';
  meta.textContent = environment.userId
    ? `Viewer: ${environment.userId}`
    : 'Viewer ID unavailable';

  const content = document.createElement('div');
  content.className = 'inspector-content';
  content.appendChild(createEmptyState('Select a single alert row from Alerts List.'));

  container.appendChild(title);
  container.appendChild(subtitle);
  container.appendChild(meta);
  container.appendChild(content);
  root.appendChild(container);

  const availableWorksheets = getAvailableWorksheets();
  void sendDebugLog('inspector-init', {
    dashboardName: environment.dashboardName,
    isMock: environment.isMock,
    configuredWorksheet: worksheetName,
    availableWorksheets,
  });

  const worksheet = findWorksheet(worksheetName);
  if (!worksheet) {
    void sendDebugLog('inspector-worksheet-not-found', {
      configuredWorksheet: worksheetName,
      availableWorksheets,
      dashboardName: environment.dashboardName,
    });
    content.innerHTML = '';
    content.appendChild(
      createEmptyState(
        `Worksheet "${worksheetName}" was not found. Available: ${availableWorksheets.join(', ')}`
      )
    );
    return;
  }

  void sendDebugLog('inspector-worksheet-bound', {
    configuredWorksheet: worksheetName,
    dashboardName: environment.dashboardName,
  });

  const updateSelection = async (): Promise<void> => {
    const marksTable = await getSelectedMarks(worksheet);
    const markData = marksTable
      ? mapSelectedMark(marksTable, {})
      : { key: null, fields: {} };
    void sendDebugLog('inspector-selection-fetch', {
      worksheetName,
      hasSelection: Boolean(marksTable),
      rowCount: marksTable?.totalRowCount ?? 0,
      selection: markData,
    });
    renderFields(content, markData.fields);
  };

  subscribeToSelectionChanges(worksheet, (marksTable) => {
    const markData = marksTable
      ? mapSelectedMark(marksTable, {})
      : { key: null, fields: {} };
    void sendDebugLog('inspector-selection-event', {
      worksheetName,
      hasSelection: Boolean(marksTable),
      rowCount: marksTable?.totalRowCount ?? 0,
      selection: markData,
    });
    renderFields(content, markData.fields);
  });

  void updateSelection();
}
